import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Cross1Icon, PlusIcon } from '@radix-ui/react-icons';
import { Avatar, Dialog, ElevatedButton } from '@producer/ui-kit';
import { IconButton } from '@producer/ui-kit';

import { getFullName } from '../../../lib/utils/get-full-name';
import { ContactSearch } from './contact-search';
import { AssignAddedContactForm } from './assign-added-contact.form';
import { useResponsive } from '../../../hooks/useResponsive';
import { ModalHeader } from '../../../components/form-builder-modal/modal-header';
import { getNameInitials } from '../../../lib/utils/get-name-initials';

import { Space } from '../../../app/entities/space';
import type { TeamContact } from '../../teams/models/team';
import type { Candidate } from '../../../app/entities/candidate';
import { Contact } from '../../../app/entities';

import styles from './assign-contacts.modal.module.scss';
type AssignContactsModalProps = {
  addedContacts: Array<TeamContact | Candidate>;
  assignedContacts?: TeamContact[];
  onAdd: (contactId: string, role?: string) => Promise<void>;
  onDelete?: (contactId: string) => void;
  spaceId: string;
  onCancel: () => void;
  title: string;
  subtitle: string;
};

export const AssignContactsModal: React.FC<AssignContactsModalProps> = observer(
  ({ spaceId, title, subtitle, addedContacts, assignedContacts, onCancel, onAdd, onDelete }) => {
    const space = Space.getOne(spaceId);
    const { isMobile, isDesktop } = useResponsive();

    const contacts = space?.contacts || [];

    const [displayedPage, setDisplayedPage] = useState<'select-contact' | 'create-contact'>(
      'select-contact',
    );
    const [hasAddedContact, setHasAddedContact] = useState(false);

    const handleAdd = async (contactId: string, role?: string) => {
      await onAdd(contactId, role);
      setHasAddedContact(true);
    };

    useEffect(() => {
      if (isDesktop) {
        setDisplayedPage('select-contact');
      }
    }, [isDesktop]);

    const assignedContactsIds = assignedContacts?.map((candidate) => candidate._id) || [];
    const addedContactsIds =
      addedContacts?.map((candidate) =>
        'contactId' in candidate ? candidate.contactId : candidate._id,
      ) || [];

    const existingContactIds = new Set([...assignedContactsIds, ...addedContactsIds]);

    const getAvatar = (candidate: Candidate) => {
      if (candidate.avatar) return candidate.avatar;

      const contact = Contact.getOne(candidate.contactId);
      return contact?.avatar || '';
    };

    return (
      <Dialog key="assign-contact-modal" onClose={onCancel} mobilePosition="bottom" hideClose>
        <Container>
          <LeftContainer data-active={displayedPage === 'select-contact'}>
            <LeftHeader title={title} onClose={onCancel} action="Done" onAction={onCancel} />

            <LeftContent>
              <ContactSearch
                contactsDetails={contacts.map((contact) => ({
                  contact,
                  isAssigned: existingContactIds.has(contact._id),
                }))}
                onAdd={handleAdd}
              />

              <AddedContactsSection>
                <Title>{subtitle}</Title>

                <AddedCandidatesContainer>
                  {addedContacts &&
                    (addedContacts?.length ? (
                      addedContacts.map((addedContact) => (
                        <Member key={`candidates-${addedContact._id}`}>
                          <TextWrapper>
                            <Avatar
                              src={getAvatar(addedContact as Candidate)}
                              initials={getNameInitials(addedContact)}
                              size="medium"
                              theme="dark"
                            />

                            <Name>{getFullName(addedContact)}</Name>
                          </TextWrapper>

                          <Invite onClick={() => onDelete && onDelete(addedContact._id)}>
                            <Cross1Icon />
                          </Invite>
                        </Member>
                      ))
                    ) : (
                      <Title>No candidates added yet</Title>
                    ))}
                </AddedCandidatesContainer>
              </AddedContactsSection>
              <CloseButtonWrapper>
                <ElevatedButton onClick={onCancel} text={hasAddedContact ? 'Save' : 'Close'} />
              </CloseButtonWrapper>
            </LeftContent>

            <CreateContactLinkWrapper>
              <span>Create contact</span>

              <IconButton
                icon={<PlusIcon />}
                onClick={() => {
                  setDisplayedPage('create-contact');
                }}
              />
            </CreateContactLinkWrapper>
          </LeftContainer>

          <RightContainer data-active={displayedPage === 'create-contact'}>
            <ModalHeader
              title="Create contact"
              action="Create"
              onAction={() => {
                const submitButton = document.getElementById('assign-added-contact-form-button');
                if (submitButton) submitButton.click();
                else onCancel();
              }}
              onClose={
                isMobile
                  ? () => {
                      setDisplayedPage('select-contact');
                    }
                  : onCancel
              }
            />

            <AssignAddedContactForm
              spaceId={spaceId}
              onAssignCandidate={handleAdd}
              onSuccess={onCancel}
            />
          </RightContainer>
        </Container>
      </Dialog>
    );
  },
);

const Container = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['container']} ${className}` : styles['container']}
    {...props}
  />
);

const LeftContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['left-container']} ${className}` : styles['left-container']}
    {...props}
  />
);

const LeftContent = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['left-content']} ${className}` : styles['left-content']}
    {...props}
  />
);

const RightContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['right-container']} ${className}` : styles['right-container']}
    {...props}
  />
);

const AddedCandidatesContainer = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className
        ? `${styles['added-candidates-container']} ${className}`
        : styles['added-candidates-container']
    }
    {...props}
  />
);

const Title = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['title']} ${className}` : styles['title']} {...props} />
);

export const Member = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['member']} ${className}` : styles['member']} {...props} />
);

export const TextWrapper = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['text-wrapper']} ${className}` : styles['text-wrapper']}
    {...props}
  />
);

export const Invite = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['invite']} ${className}` : styles['invite']} {...props} />
);

export const Name = ({ className, ...props }: React.ComponentPropsWithoutRef<'h6'>) => (
  <h6 className={className ? `${styles['name']} ${className}` : styles['name']} {...props} />
);

const CloseButtonWrapper = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className ? `${styles['close-button-wrapper']} ${className}` : styles['close-button-wrapper']
    }
    {...props}
  />
);

const CreateContactLinkWrapper = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className
        ? `${styles['create-contact-link-wrapper']} ${className}`
        : styles['create-contact-link-wrapper']
    }
    {...props}
  />
);

// Was `styled(ModalHeader)`: renders ModalHeader with its own class layered on.
const LeftHeader = ({ className, ...props }: React.ComponentProps<typeof ModalHeader>) => (
  <ModalHeader
    className={className ? `${styles['left-header']} ${className}` : styles['left-header']}
    {...props}
  />
);

const AddedContactsSection = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className
        ? `${styles['added-contacts-section']} ${className}`
        : styles['added-contacts-section']
    }
    {...props}
  />
);
